// Pure booking rules: capacity math and date arithmetic.
// CommonJS on purpose so scripts/check-booking-rules.js can require it with no
// transpiler; webpack resolves named imports from CommonJS fine.
const moment = require('moment-timezone')
// One direction only: serviceArea requires nothing at all, deliberately, so it
// can be read without moment. Nothing there imports this file back.
const { isFullyServedProvince } = require('./serviceArea')

const TZ = 'Asia/Bangkok'
const PER_CAR = { am: 2, pm: 3, total: 5 }
const DEFAULT_CARS = 5
const LEAD_WORKING_DAYS = 4
const BOOKING_HORIZON_DAYS = 60
const MAX_HORIZON_DAYS = 365

// How many jobs one car carries in each window. PER_CAR is only the starting
// point now -- the real rate is a standing setting in bookingConfig, and a day
// edited through the range editor keeps a snapshot of whatever it was then.
// Every value therefore arrives from a text field: coerced per key, so one bad
// entry cannot take the other two down with it, and a deliberate 0 survives.
function normalizePerCar (perCar) {
  const one = key => {
    const v = perCar ? perCar[key] : undefined
    // Absent is not zero. Number(null) and Number('') are both 0, so without
    // this a missing key -- or a cleared input -- would silently close that
    // window instead of falling back to the rate we know. An explicit 0 still
    // means zero.
    if (v == null || v === '') return PER_CAR[key]
    const n = Math.floor(Number(v))
    return isFinite(n) ? Math.max(0, n) : PER_CAR[key]
  }
  return { am: one('am'), pm: one('pm'), total: one('total') }
}

// A day's caps are derived from how many cars are running that day and what one
// car carries. 5 cars at the standing rate -> { am: 10, pm: 15, total: 25 }.
function capacityForCars (cars, perCar) {
  const n = Math.max(0, Math.floor(Number(cars) || 0))
  const p = normalizePerCar(perCar)
  return { am: n * p.am, pm: n * p.pm, total: n * p.total }
}

// A day's ORDINARY capacity: the fleet available that day, less the trucks held
// by out-of-area trips, at that day's rate.
//
// Derived on every read rather than snapshotted onto the day document, so the
// จำนวนรถ range editor keeps meaning "the fleet available that day" and a trip
// opened afterwards still takes its truck out of whatever that number is.
// Snapshotting would need every trip open and close to rewrite every affected
// day, and a missed one would be silently wrong for a month.
//
// capacityForCars clamps a negative car count to zero, so reserving more trucks
// than the fleet holds produces a closed day rather than negative caps -- which
// would read as "everything is full, everywhere" with no diagnostic.
//
// Lives here rather than in bookingService.capsForDay, which is where it used
// to be, because that file imports firebase and so cannot be require()d by
// check-booking-rules. A rule nothing can assert is a rule that drifts.
function capacityForDay (dayDoc, configCars, configPerCar) {
  const day = dayDoc || {}
  const cars = day.cars != null ? day.cars : configCars
  const perCar = day.perCar ? day.perCar : configPerCar
  const reserved = reservedCars(day) + carriedCars(day)
  return capacityForCars((Math.floor(Number(cars)) || 0) - reserved, perCar)
}

// Staff set only the two windows; the daily total is their sum. Deriving it
// rather than accepting it is what keeps the three numbers consistent -- there
// is no way to enter a total that contradicts the windows it is made of.
//
// Applied to INPUT only. normalizePerCar still reads a stored rate exactly as
// written, so a day snapshotted under an older rule keeps the caps it was given.
function withDerivedTotal (perCar) {
  const p = normalizePerCar(perCar)
  return { am: p.am, pm: p.pm, total: p.am + p.pm }
}

function isSunday (dateStr) {
  return moment.tz(dateStr, TZ).day() === 0
}

// Nothing is delivered on a Sunday unless delivery opens that one Sunday on
// bookingDate (setSundayOpen), when the week is full and an extra day helps.
// An opened Sunday is then an ordinary day: its cars, ปิดรับจอง and capacity
// apply as on any weekday. Only a real `true` opens one -- the same reason only
// a real `true` stops a day. The lead time still counts Monday to Saturday.
function isSundayOpen (dayDoc) {
  return !!dayDoc && dayDoc.sundayOpen === true
}

// The test every booking path asks instead of isSunday: a Sunday nobody opened.
function isClosedSunday (dateStr, dayDoc) {
  return isSunday(dateStr) && !isSundayOpen(dayDoc)
}

// Opening asks for the cars that Sunday runs -- usually fewer than a weekday.
function checkSundayOpen (dateStr, req) {
  const r = req || {}
  if (!isSunday(dateStr)) return { ok: false, reason: 'NOT_SUNDAY' }
  const cars = Math.floor(Number(r.cars))
  if (!isFinite(cars) || cars < 1) return { ok: false, reason: 'BAD_CARS' }
  return { ok: true, cars: cars, note: String(r.note || '').trim() }
}

// Closing an opened Sunday is ปิดรับจอง, and there it must say why: shops were
// told the day was open, and whoever answers them next needs the reason.
function stopNoteRequired (dateStr) {
  return isSunday(dateStr)
}

function todayStr () {
  return moment.tz(TZ).format('YYYY-MM-DD')
}

// The last day the calendar opens. BOOKING_HORIZON_DAYS is only the starting
// point, exactly as LEAD_WORKING_DAYS below is: how far ahead the operation is
// willing to commit a fleet it has not yet planned is a business decision that
// moves, so the real number is a standing setting and this is where it lands.
function horizonDate (fromStr, horizonDays) {
  return moment.tz(fromStr, TZ).add(normalizeHorizonDays(horizonDays), 'day').format('YYYY-MM-DD')
}

// Bounded HERE, not only in the settings form. Firestore rules are open, so a
// hand-edited config document is a real input: 0 would leave a calendar with no
// day in it at all, and a stray 60000 would let the month arrows walk for 164
// years. One year is the ceiling because nothing plans a delivery fleet past it.
function normalizeHorizonDays (days) {
  if (days == null || days === '') return BOOKING_HORIZON_DAYS
  const n = Math.floor(Number(days))
  if (!isFinite(n)) return BOOKING_HORIZON_DAYS
  return Math.min(MAX_HORIZON_DAYS, Math.max(1, n))
}

// How many working days must pass after the order before delivery can be
// booked. LEAD_WORKING_DAYS is only the starting point: the operation shortens
// or lengthens it as the workload demands, so the real number is a standing
// setting. Arrives from a text field, hence the coercion -- and 0, meaning
// same-day, is a deliberate value, not a missing one.
function normalizeLeadDays (days) {
  if (days == null || days === '') return LEAD_WORKING_DAYS
  const n = Math.floor(Number(days))
  return isFinite(n) ? Math.max(0, n) : LEAD_WORKING_DAYS
}

// Step forward one day at a time, counting only non-Sundays, until `leadDays`
// are counted. Saturday counts as a working day. With a lead above zero the
// result can never land on a Sunday, by construction; with zero it is the order
// date itself, which isSunday() rejects separately if that day is a Sunday.
function earliestDeliveryDate (orderDateStr, leadDays) {
  const want = normalizeLeadDays(leadDays)
  const d = moment.tz(orderDateStr, TZ).startOf('day')
  let counted = 0
  while (counted < want) {
    d.add(1, 'day')
    if (d.day() !== 0) counted++
  }
  return d.format('YYYY-MM-DD')
}

// Which days of one calendar month a booking grid may draw. The month is the
// unit the user navigates in, but the bookable window is [earliest, horizon] --
// so the month holding `earliest` starts on that date rather than on the 1st,
// and the month holding the horizon stops there. The grid used to draw the
// whole month and grey out the rest, which meant opening the page late in the
// month put three weeks of dead cards above the first day anyone could pick.
// Returns null when month and window do not overlap: that is a month with
// nothing to offer, and the caller says so instead of drawing an empty grid.
// ISO dates compare lexicographically, which is also chronologically.
function bookingMonthRange (monthStartStr, earliestStr, horizonStr) {
  const m = moment.tz(monthStartStr, TZ)
  const first = m.clone().startOf('month').format('YYYY-MM-DD')
  const last = m.clone().endOf('month').format('YYYY-MM-DD')
  const from = earliestStr && earliestStr > first ? earliestStr : first
  const to = horizonStr && horizonStr < last ? horizonStr : last
  return from > to ? null : { from: from, to: to }
}

// The low end of the window, and the only bound in the whole set that is not
// relative to something else: TOO_EARLY is measured from the order date and
// TOO_LATE from the horizon, so between them nothing ever said "not in the
// past". A booking made weeks ago carries an order date weeks old, which is
// what let its owner move it backwards onto a day that has already happened.
// Whichever of the two bounds falls later wins. `today` is injectable because
// the assertions have to pin it; callers pass nothing and get the real clock.
//
// It can return a Sunday, when today is one. That is deliberate -- SUNDAY is a
// separate rule with its own message, and folding it in here would report a
// day that has passed as a day off.
function firstBookableDate (orderDateStr, leadDays, todayDateStr) {
  const earliest = earliestDeliveryDate(orderDateStr, leadDays)
  const today = todayDateStr || todayStr()
  return earliest > today ? earliest : today
}

// The lead time and the horizon are set independently but they are not
// independent: a lead longer than the horizon leaves a calendar with nothing
// selectable in it and nothing on screen to explain why. The settings form asks
// this before it saves either one.
function hasBookableDay (fromStr, leadDays, horizonDays) {
  return earliestDeliveryDate(fromStr, leadDays) <= horizonDate(fromStr, horizonDays)
}

const SLOTS = ['full', 'am', 'pm']                       // ทั้งวัน first: it is the preferred choice
const SLOT_LABEL_TH = { full: 'ทั้งวัน', am: 'เช้า', pm: 'บ่าย' }
const SLOT_TIME = { full: '08:00 - 17:00', am: '08:00 - 12:00', pm: '13:00 - 17:00' }

const BOOKING_ERROR_TH = {
  HAS_RT: 'การจองนี้มีเลขที่ RT แล้ว',
  NOT_EXCHANGEABLE: 'เพิ่ม RT ได้เฉพาะการจองที่มีเลขที่ SO',
  RT_TAKEN: 'เลขที่ RT นี้มีการจองอยู่แล้ว',
  DAY_FULL:  'วันที่เลือกเต็มแล้ว โปรดเลือกวันอื่น',
  AM_FULL:   'ช่วงเช้าของวันที่เลือกเต็มแล้ว',
  PM_FULL:   'ช่วงบ่ายของวันที่เลือกเต็มแล้ว',
  SUNDAY:    'ไม่มีการจัดส่งในวันอาทิตย์ (เว้นแต่ฝ่ายจัดส่งเปิดรับเป็นพิเศษ)',
  NOT_SUNDAY: 'เปิดรับพิเศษได้เฉพาะวันอาทิตย์',
  BAD_CARS:  'โปรดระบุจำนวนรถอย่างน้อย 1 คัน',
  STOP_NOTE_REQUIRED: 'โปรดระบุเหตุผลที่ปิดรับจอง',
  PAST:      'วันที่เลือกผ่านไปแล้ว โปรดเลือกตั้งแต่วันนี้เป็นต้นไป',
  // Both numbers are filled in by errorMessage(): hard-coding 4 and 60 here
  // would start lying the moment either setting moved off them.
  TOO_EARLY: 'จองได้หลังจากวันที่สั่งซื้อ {n} วันทำการ',
  TOO_LATE:  'จองล่วงหน้าได้ไม่เกิน {h} วัน',
  NOT_FOUND: 'ไม่พบรายการจองนี้',
  ALREADY_CANCELLED: 'รายการนี้ถูกยกเลิกไปแล้ว',
  SAME_SLOT: 'เลือกวันและช่วงเวลาเดิม ไม่มีอะไรเปลี่ยนแปลง',
  NOT_ALLOWED: 'คุณไม่มีสิทธิ์จัดการรายการจองนี้',
  NO_REASON: 'โปรดเลือกเหตุผลในการย้าย',
  NO_CHANGES: 'ไม่มีข้อมูลที่เปลี่ยนแปลง',
  BAD_GEO: 'รูปแบบพิกัดไม่ถูกต้อง เช่น 13.7563, 100.5018',
  BAD_SLOT: 'ช่วงเวลาที่เลือกไม่ถูกต้อง',
  NO_SO: 'โปรดระบุเลขที่ SO',
  HAS_SO: 'รายการนี้มีเลขที่ SO อยู่แล้ว หากต้องการเปลี่ยน โปรดติดต่อฝ่ายจัดส่ง',
  SO_TAKEN: 'เลขที่ SO นี้ถูกใช้กับการจองอื่นแล้ว',
  DAY_STOPPED: 'วันที่เลือกปิดรับการจองแล้ว โปรดเลือกวันอื่น',
  // --- out-of-area trips -----------------------------------------------------
  // TRIP_CLOSED_ERR, not TRIP_CLOSED: the latter is the status string a trip
  // document carries, and one name for two things is how a status ends up
  // rendered to a customer as an error.
  TRIP_FULL: 'เส้นทางนอกพื้นที่ของวันที่เลือกเต็มแล้ว โปรดเลือกวันอื่น',
  TRIP_CLOSED_ERR: 'เส้นทางนอกพื้นที่ของวันที่เลือกปิดรับแล้ว โปรดติดต่อฝ่ายจัดส่ง',
  NO_TRIP: 'วันที่เลือกไม่มีเส้นทางไปพื้นที่นี้',
  TRIP_DATE: 'เส้นทางนี้ไม่ใช่วันเดียวกับการจอง',
  TRIP_PROVINCE: 'เส้นทางนี้ไม่ผ่านจังหวัดของการจองนี้',
  TRIP_HAS_BOOKINGS: 'เส้นทางนี้มีการจองอยู่ ย้ายหรือยกเลิกการจองก่อนจึงจะยกเลิกเส้นทางได้',
  TRIP_EXISTS: 'วันที่เลือกมีเส้นทางไปจังหวัดนี้อยู่แล้ว',
  TRIP_HAS_PROVINCE: 'เส้นทางนี้ไปจังหวัดที่เลือกอยู่แล้ว',
  TRIP_LAST_PROVINCE: 'เส้นทางต้องมีอย่างน้อยหนึ่งจังหวัด หากไม่ใช้เส้นทางนี้แล้ว โปรดยกเลิกเส้นทาง',
  PROVINCE_HAS_BOOKINGS: 'จังหวัดนี้มีการจองในเส้นทางอยู่ ย้ายหรือยกเลิกการจองก่อนจึงจะนำจังหวัดออกได้',
  TRIP_STALE: 'มีการจองเข้าเส้นทางนี้ระหว่างทำรายการ โปรดลองใหม่อีกครั้ง',
  TRIP_CANCELLED: 'เส้นทางนี้ถูกยกเลิกแล้ว หากต้องการใช้อีกครั้ง โปรดเปิดเส้นทางใหม่',
  NO_CARS: 'รถไม่พอสำหรับเปิดเส้นทางในวันที่เลือก',
  NO_PROVINCES: 'โปรดเลือกจังหวัดปลายทางของเส้นทาง',
  BAD_CAPACITY: 'จำนวนงานต่อเส้นทางไม่ถูกต้อง',
  PROVINCE_IN_AREA: 'จังหวัดนี้อยู่ในพื้นที่จัดส่งปกติอยู่แล้ว ไม่ต้องเปิดเส้นทาง',
  // --- propose and confirm (proposalRules.js) ----------------------------------
  PROPOSAL_PENDING: 'การจองนี้มีคำขอรอยืนยันอยู่ โปรดยืนยันหรือปฏิเสธคำขอก่อน',
  OWN_BOOKING: 'การจองนี้เป็นของคุณ ย้ายหรือยกเลิกได้โดยตรงที่หน้าจองวันส่งสินค้า',
  STAFF_ACTS_DIRECTLY: 'เจ้าหน้าที่ย้ายหรือยกเลิกการจองได้โดยตรง ไม่ต้องส่งคำขอ',
  DISPATCHED: 'งานนี้จัดรถแล้ว โปรดแจ้งฝ่ายจัดส่ง',
  NO_PROPOSAL: 'ไม่มีคำขอที่รอดำเนินการ อาจมีผู้อื่นดำเนินการไปแล้ว',
  NOT_DECIDER: 'คุณไม่มีสิทธิ์ยืนยันหรือปฏิเสธคำขอนี้',
  NO_REJECT_REASON: 'โปรดระบุเหตุผลที่ปฏิเสธ',
  NOT_PROPOSER: 'ถอนคำขอได้เฉพาะผู้ส่งคำขอหรือผู้มีสิทธิ์ยืนยัน',
  EXPIRED: 'คำขอนี้หมดอายุแล้ว เพราะวันที่ขอเลื่อนผ่านไปแล้ว โปรดปิดคำขอแล้วย้ายการจองใหม่',
  // --- one destination, several bookings (visitRules.js) ------------------------
  VISIT_SELF: 'เลือกการจองอื่นเพื่อรวมจุดส่ง',
  VISIT_LINKED: 'การจองนี้รวมจุดส่งกับการจองอื่นอยู่แล้ว โปรดแยกจุดส่งก่อน',
  VISIT_SLOT: 'รวมจุดส่งได้เฉพาะการจองวันเดียวกันและช่วงเวลาเดียวกัน',
  VISIT_KEY: 'รวมจุดส่งได้เฉพาะลูกค้าเบอร์โทรและตำบลเดียวกัน',
  VISIT_TRIP: 'รวมจุดส่งนอกพื้นที่ได้เฉพาะการจองในเส้นทางเดียวกัน',
  NOT_IN_VISIT: 'การจองนี้ไม่ได้รวมจุดส่งกับการจองอื่น',
  // Up-country (large truck) orders -- see upcountry.js.
  UPCOUNTRY_PATH: 'ออเดอร์รถใหญ่ต่างจังหวัด ใช้ปุ่มของออเดอร์นี้หรือหน้ารถใหญ่ต่างจังหวัด',
  UP_NO_SO: 'ต่างจังหวัด (รถใหญ่) ต้องมีเลขที่ SO',
  UP_REFUSED_PROVINCE: 'ไม่มีบริการจัดส่งจังหวัดนี้',
  UP_NO_GROUP: 'จังหวัดนี้ไม่อยู่ในเส้นทางรถใหญ่',
  UP_BAD_WEEK: 'สัปดาห์ที่เลือกไม่ถูกต้อง หรือจัดรอบไปแล้ว',
  UP_NO_VALUE: 'ไม่พบมูลค่าออเดอร์จาก SAP โปรดติดต่อฝ่ายไอที',
  UP_NOT_ALLOWED: 'ไม่มีสิทธิ์แก้ไขออเดอร์นี้',
  UP_BAD_MODE: 'รูปแบบการส่งไม่ถูกต้อง',
  UP_ON_TRIP: 'ออเดอร์นี้อยู่ในรอบรถแล้ว ใช้ "เลื่อนไปรอบถัดไป"',
  UP_NOT_ON_TRIP: 'ออเดอร์นี้ยังไม่อยู่ในรอบรถ',
  UP_NO_ORDERS: 'ยังไม่ได้เลือกออเดอร์',
  UP_NO_CAR: 'ยังไม่ได้เลือกรถ',
  UP_PAST: 'วันโหลดสินค้าผ่านไปแล้ว',
  UP_SUNDAY: 'ไม่ออกรถวันอาทิตย์',
  UP_BAD_RETURN: 'วันกลับต้องไม่ก่อนวันออกรถ',
  UP_NOT_POOLED: 'มีออเดอร์ที่ถูกยกเลิกหรือถูกจัดรอบไปแล้ว โปรดโหลดใหม่',
  UP_WRONG_GROUP: 'มีออเดอร์ที่ไม่อยู่ในกลุ่มเส้นทางนี้',
  UP_TOO_EARLY_WEEK: 'มีออเดอร์ที่ลูกค้ายังไม่พร้อมรับในสัปดาห์นี้',
  UP_TRUCK_BUSY: 'รถคันนี้ติดรอบอื่นในช่วงวันดังกล่าว',
  UP_TRIP_SENT: 'รอบนี้ส่งต่อให้จัดรถแล้ว โปรดย้อนสถานะก่อน',
  UP_SHEET_CHANGED: 'ใบจัดรถของรอบนี้เปลี่ยนไประหว่างทำรายการ — โปรดลองอีกครั้ง',
  UP_SENT_LOCKED: 'รอบนี้ถึงวันออกรถแล้ว — แก้ไขที่หน้าจัดรถ',
  UP_ORDER_SENT: 'ออเดอร์นี้อยู่ในรอบรถใหญ่ที่ส่งเข้าจัดรถแล้ว โปรดแจ้งฝ่ายจัดส่ง (ฝ่ายจัดส่ง: ยกเลิกที่หน้า รถใหญ่ต่างจังหวัด — รอบที่ทำเครื่องหมายส่งเองให้กด ย้อนสถานะ ก่อน)',
  UP_LAST_ORDER: 'เป็นออเดอร์สุดท้ายของรอบ — ใช้ ยกเลิกการส่ง หรือ ยกเลิกรอบ แทน',
  UP_PLAN_STALE: 'รายการออเดอร์ของรอบนี้เปลี่ยนไปแล้ว หรือรอบไม่ได้อยู่ในสถานะจัดรอบ — โปรดวางแผนใหม่',
  UP_PLAN_BAD: 'แผนเส้นทางไม่ถูกต้อง โปรดวางแผนใหม่',
  UP_PLAN_NONE: 'รอบนี้ไม่มีแผนเส้นทางให้ยกเลิก',
  UP_PLAN_SENT: 'รอบนี้ส่งต่อจัดรถแล้ว หรือไม่ได้อยู่ในสถานะจัดรอบ — ยกเลิกแผนไม่ได้',
  UP_SEND_STATUS: 'รอบนี้ไม่ได้อยู่ในสถานะที่ส่งเข้าจัดรถได้',
  UP_SEND_PROBLEMS: 'มีออเดอร์ที่ส่งเข้าจัดรถไม่ได้ — ดูรายการด้านล่าง',
  UP_SEND_CLASH: 'เลขที่ใบจัดรถนี้ถูกใช้ไปแล้ว — กด ยกเลิกการส่ง แล้วส่งใหม่',
  UP_SEND_NOT_SENT: 'รอบนี้ไม่ได้ถูกส่งเข้าจัดรถด้วยปุ่มนี้',
  SHOP_BAD_CODE: 'โปรดใส่รหัสลูกค้า SAP (ไม่มีช่องว่างหรือ /)',
  SHOP_BAD_NAME: 'โปรดใส่ชื่อร้าน',
  SHOP_BAD_DAYS: 'วันปิดร้านไม่ถูกต้อง',
  UP_BAD_ACTUAL_RETURN: 'วันที่รถกลับจริงต้องอยู่ระหว่างวันออกรถถึงวันนี้',
  UP_TRIP_CLOSED: 'รอบนี้ถูกยกเลิกหรือปิดไปแล้ว',
  UP_TRIP_FULL: 'รอบนี้เต็มแล้ว (เกินความจุรถ) โปรดเลือกรอบอื่นหรือเข้าคิวรอจัดรอบ'
}

// The only place error copy should be read from, because one message needs a
// number that lives in the config rather than in the string.
function errorMessage (reason, ctx) {
  const raw = BOOKING_ERROR_TH[reason]
  if (!raw) return reason
  const n = normalizeLeadDays(ctx && ctx.leadDays)
  const h = normalizeHorizonDays(ctx && ctx.horizonDays)
  return raw.replace('{n}', n).replace('{h}', h)
}

class BookingError extends Error {
  constructor (reason) {
    // errorMessage, not the raw map: TOO_EARLY carries a {n} placeholder that
    // would otherwise reach a user verbatim through Error.message.
    super(errorMessage(reason))
    this.name = 'BookingError'
    this.reason = reason
  }
}

// ทั้งวัน is checked against the day total only -- it never consumes เช้า/บ่าย
// quota, which is precisely what keeps it available longest.
// Comparisons are >= so an over-capacity day (total > cap) still reads as full.
function checkCapacity (counts, caps, slot) {
  const c = Object.assign({ am: 0, pm: 0, fullDay: 0, total: 0 }, counts || {})
  if (c.total >= caps.total) return { ok: false, reason: 'DAY_FULL' }
  if (slot === 'am' && c.am >= caps.am) return { ok: false, reason: 'AM_FULL' }
  if (slot === 'pm' && c.pm >= caps.pm) return { ok: false, reason: 'PM_FULL' }
  return { ok: true }
}

// The days the จำนวนรถ editor writes: every day of the range but a Sunday that
// was not opened. `byDate` is the bookingDay documents of the range.
function capacityDates (from, to, byDate) {
  const days = byDate || {}
  return datesInRange(from, to, { skipSundays: false }).filter(d => !isClosedSunday(d, days[d]))
}

// Inclusive date range. Sundays are dropped by default because nothing is
// delivered on a Sunday, so capacity overrides must not be written for them.
function datesInRange (from, to, opts) {
  const skipSundays = !opts || opts.skipSundays !== false
  const out = []
  const d = moment.tz(from, TZ).startOf('day')
  const end = moment.tz(to, TZ).startOf('day')
  while (d.isSameOrBefore(end)) {
    if (!skipSundays || d.day() !== 0) out.push(d.format('YYYY-MM-DD'))
    d.add(1, 'day')
  }
  return out
}

// The administrative area is stored as one "subDt,district,province" string.
// carBooking.vue:1560 writes exactly this format and splits it back apart for
// display, so the two pages must agree byte for byte -- these helpers are the
// shared definition of that contract.
const BANGKOK = 'กรุงเทพมหานคร'

function formatLocation (entry) {
  if (!entry) return ''
  return [entry.subDt, entry.district, entry.province].join(',')
}

function parseLocation (str) {
  if (!str || typeof str !== 'string') return null
  const parts = str.split(',')
  if (parts.length !== 3) return null
  return { subDt: parts[0], district: parts[1], province: parts[2] }
}

// Bangkok is divided into แขวง inside เขต; every other province uses ตำบล
// inside อำเภอ. Getting this wrong reads as obviously foreign to a Thai user.
function locationLabel (entry) {
  if (!entry) return ''
  const bkk = entry.province === BANGKOK
  return (bkk ? 'แขวง' : 'ตำบล') + entry.subDt +
    ' / ' + (bkk ? 'เขต' : 'อำเภอ') + entry.district +
    ' / ' + entry.province
}

// Coordinates are pasted straight out of Google Maps, which copies them as
// "13.7563, 100.5018". Anything that is not a plain decimal pair inside real
// lat/lng bounds is rejected rather than stored -- a half-parsed coordinate
// sends a driver somewhere confidently wrong, which is worse than no coordinate.
function parseGeo (str) {
  if (!str || typeof str !== 'string') return null
  const parts = str.trim().split(',')
  if (parts.length !== 2) return null
  const lat = Number(parts[0].trim())
  const lng = Number(parts[1].trim())
  if (!isFinite(lat) || !isFinite(lng)) return null
  if (parts[0].trim() === '' || parts[1].trim() === '') return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  return { lat: lat, lng: lng }
}

function formatGeo (geo) {
  if (!geo) return ''
  return geo.lat + ',' + geo.lng
}

// Until U_KSK_Geolocation / U_KSK_Landmark were added (see soPin below), SAP had
// no field for a landmark or a coordinate: U_KSK_SA03 is "Cus. Address",
// a single varchar(200). Staff therefore append the landmark to the address
// itself after a "จุดสังเกต" marker, and occasionally that landmark is a
// coordinate pair. Splitting it here means the page can show the address, the
// landmark and the coordinate as three separate, correctable fields.
//
// The convention is young -- of the 18 orders that use it, 14 were written in a
// single month -- so expect the share to grow, and expect it to be typed by
// hand: the marker appears both as จุดสังเกต and as the misspelled จุดสังเกตุ,
// with or without a following colon.
const LANDMARK_MARKER = 'จุดสังเกต'

function parseCustAddress (raw) {
  const empty = { address: '', landmark: '', geo: '' }
  if (!raw || typeof raw !== 'string') return empty

  const at = raw.indexOf(LANDMARK_MARKER)
  if (at === -1) return { address: raw.trim(), landmark: '', geo: '' }

  const address = raw.slice(0, at).trim()
  // Strip the marker, then any stray ุ from the จุดสังเกตุ spelling, then the
  // colon and surrounding space.
  let rest = raw.slice(at + LANDMARK_MARKER.length).replace(/^[ุ\s:：]+/, '').trim()

  // A leading coordinate pair is lifted out; whatever follows stays as the note.
  const m = rest.match(/^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*([\s\S]*)$/)
  if (m) {
    const geo = parseGeo(m[1] + ',' + m[2])
    if (geo) return { address: address, landmark: m[3].trim(), geo: formatGeo(geo) }
  }
  return { address: address, landmark: rest, geo: '' }
}

// SAP now has real fields for both: GetSo returns U_KSK_Geolocation as `geo`
// (plain "lat,lng") and U_KSK_Landmark as `landmark`. Typed by hand, so the pin
// is validated here -- one that does not parse is dropped rather than sent to a
// driver -- and written back in the one form the rest of the chain reads.
function soPin (so) {
  const point = parseGeo(so && so.geo)
  const landmark = so && typeof so.landmark === 'string' ? so.landmark.trim() : ''
  return { geo: point ? formatGeo(point) : '', landmark: landmark }
}

// SAP's column widths: U_KSK_Geolocation NVarChar(50), U_KSK_Landmark
// NVarChar(200). The ASMX side refuses a longer value, so the forms cap
// จุดสังเกต at this length rather than let a save half-succeed.
const SAP_LANDMARK_MAX = 200

// What UpdateSoPin should write after a person changed พิกัด / จุดสังเกต:
// `null` for a field that did not change (the ASMX method leaves it alone), the
// new value -- '' to clear -- for one that did. `null` overall when neither
// changed, so the caller makes no call. Compared canonically, the way both are
// stored, so re-saving an unchanged pin written with a space is not a change.
function sapPinPatch (before, after) {
  const geoOf = v => { const p = parseGeo(v && v.geo); return p ? formatGeo(p) : '' }
  const lmOf = v => (v && typeof v.landmark === 'string' ? v.landmark.trim() : '')
  const geo = geoOf(after) !== geoOf(before) ? geoOf(after) : null
  const landmark = lmOf(after) !== lmOf(before) ? lmOf(after) : null
  return geo === null && landmark === null ? null : { geo: geo, landmark: landmark }
}

// The other direction: what SAP holds that this booking does not. A shop can
// correct a customer's pin in the order system long after the delivery date was
// booked, and that write reaches ORDR at once -- so SAP, not the booking, is
// where the newer value lives. Returns only the fields SAP actually carries and
// that differ, or null when there is nothing to offer.
//
// A field SAP is blank on is never offered: blank means "not recorded" there,
// and a พิกัด staff dropped by hand is usually the only one anybody has. That is
// the same asymmetry mergeSoFields keeps.
function sapPinUpdate (current, sapPin) {
  const point = parseGeo(current && current.geo)
  const geoNow = point ? formatGeo(point) : ''
  const lmNow = current && typeof current.landmark === 'string' ? current.landmark.trim() : ''
  const out = {}
  if (sapPin && sapPin.geo && sapPin.geo !== geoNow) out.geo = sapPin.geo
  if (sapPin && sapPin.landmark && sapPin.landmark !== lmNow) out.landmark = sapPin.landmark
  return Object.keys(out).length ? out : null
}

// The SO numbers a pin can be written to: SAP DocNums, digits only, once each.
// A นัดหมาย has none yet, and a return row carries its RT number elsewhere.
function sapSoNumbers (values) {
  const out = []
  ;(values || []).forEach(v => {
    const s = String(v == null ? '' : v).trim()
    if (/^\d{1,10}$/.test(s) && Number(s) <= 2147483647 && out.indexOf(s) === -1) out.push(s)
  })
  return out
}

// What UpdateSoBookDate should write to a booking's SO: the delivery date the
// customer was promised while the booking is live, '' once it is cancelled --
// which is how SAP, and anything reading ORDR beside it, learns that a delivery
// is booked before dispatch assigns a truck (U_KSK_TransNo / U_KSK_DelDate are
// the later stage and are written elsewhere).
//
// Null when there is nothing to write: a นัดหมาย has no ORDR row until ใส่ SO,
// and a live booking with no date would otherwise clear the field by accident.
function sapBookDate (booking) {
  const b = booking || {}
  const so = sapSoNumbers([b.soNo])
  if (!so.length) return null
  if (!isActive(b.status)) return { so: so[0], bookDate: '' }
  const date = String(b.date == null ? '' : b.date).trim()
  return date ? { so: so[0], bookDate: date } : null
}

// sapBookDate's twin for the return (ORDN.U_KSK_BookDate), written through
// UpdateRtBookDate. An exchange writes both documents, so whichever one another
// system reads shows the stage; a return-only pickup has only this one.
function sapRtBookDate (booking) {
  const b = booking || {}
  const rt = sapSoNumbers([b.rtNo])
  if (!rt.length) return null
  if (!isActive(b.status)) return { rt: rt[0], bookDate: '' }
  const date = String(b.date == null ? '' : b.date).trim()
  return date ? { rt: rt[0], bookDate: date } : null
}

// What a booking form fills from an SO: the address with any จุดสังเกต tail
// lifted off, and a pin and landmark taken from the SAP fields first and from
// that tail second. Each falls back on its own, so an order carrying only a
// landmark in the new field keeps the coordinate still written in its address.
function soAddressFields (so) {
  const parsed = parseCustAddress(so && so.custAddress)
  const pin = soPin(so)
  return {
    address: parsed.address,
    geo: pin.geo || parsed.geo,
    landmark: pin.landmark || parsed.landmark
  }
}

// Whether the support boxes describe a search at all.
//
// The search and the day list answer different questions and the table can
// only show one, so this is the predicate that decides which it owes you: with
// a term, a search result; without one, the chosen day.
//
// It is a rule rather than an inline condition because two places need the
// same answer and used to disagree. runSearch refused an empty search, but
// nothing noticed a search being emptied AFTER it ran -- so clearing the box
// left the table on a result whose term was no longer on screen, with the live
// snapshots for the day arriving and being discarded by design.
//
// null matters as much as '': that is what Vuetify's clearable writes.
function hasSearchTerms (search) {
  const s = search || {}
  const so = s.soNo == null ? '' : String(s.soNo).trim()
  return !!(normalizeTel(s.tel) || so)
}

// Phone numbers reach us as 081-234-5678, 0812345678, +66 81 234 5678,
// "081-234-5678 ต่อ 12" and, from a foreign customer, +856 20 5555 1234. A
// support agent taking a call types whichever form the customer reads out, so
// both the stored telDigits field and the search box run through here -- they
// can only match if they normalise identically.
//
// Only the first number in the field counts: "02-123-4567, 081-234-5678" used
// to run both together and keep ten digits of the mixture, a number nobody has.
function firstTelPart (str) {
  const parts = String(str).split(/[,;/]|[A-Za-z฀-๿]+/)
  for (let i = 0; i < parts.length; i++) if (/\d/.test(parts[i])) return parts[i]
  return ''
}

// E.164 caps a full international number at 15 digits.
const FOREIGN_TEL_MAX = 15

function normalizeTel (str) {
  if (str == null) return ''
  const part = firstTelPart(str)
  let d = part.replace(/\D/g, '')
  // +856..., +95..., +60...: a foreign number keeps its country code and all
  // of its digits. Cutting it to ten, as a Thai number is cut, would make two
  // numbers in the same Lao exchange read as one.
  const plus = /^\s*\+/.test(part)
  if (plus && d.slice(0, 2) !== '66') return d.slice(0, FOREIGN_TEL_MAX)
  // +66 81... -> 081... Without the + it is guarded on length, so a 06x mobile,
  // whose digits start "06" not "66", and a 9-digit number beginning 66 are
  // both left alone; with it, +66 2 123 4567 is only ten digits and still Thai.
  // "+66 081..." -- the trunk 0 kept after the country code -- is common enough
  // to accept rather than turn into 0081...
  if (d.slice(0, 2) === '66' && (plus || d.length >= 11)) d = d.charAt(2) === '0' ? d.slice(2) : '0' + d.slice(2)
  // An extension typed into the same box would otherwise make the number
  // unsearchable; keep the dialable part.
  if (d.length > 10) d = d.slice(0, 10)
  return d
}

// What a typed phone is wrong with, in Thai, or '' when it is fine. Empty is
// fine here -- "required" is its own rule, and a form may not require it.
//
// A Thai number is 0 then 9 digits for a mobile (06/08/09) or 8 for a landline
// (02-05, 07), or the same after +66. Anything else must be written as a
// foreign number: + and the country code, 8 to 15 digits in all -- which is how
// a Lao, Myanmar or Malaysian customer's number is told from a Thai number
// typed wrong. A number saved in any other shape cannot be found again on
// ค้นหาการจอง, which decides phone-or-SO by the leading 0 or +.
const TEL_HINT = 'เบอร์ไทยขึ้นต้นด้วย 0 เช่น 081-234-5678 หรือ 02-123-4567 ' +
  'ส่วนเบอร์ต่างประเทศขึ้นต้นด้วย + และรหัสประเทศ เช่น +856 20 5555 1234'

function isThaiTel (d) {
  return /^0[689]\d{8}$/.test(d) || /^0[2-57]\d{7}$/.test(d)
}

function telProblem (str) {
  const raw = str == null ? '' : String(str).trim()
  if (!raw) return ''
  const part = firstTelPart(raw)
  if (!part) return 'ไม่พบเบอร์โทร — ' + TEL_HINT
  const compact = part.replace(/[\s\-().]/g, '')
  if (!/^\+?\d+$/.test(compact)) return 'เบอร์โทรมีอักขระที่ไม่ใช่ตัวเลข — ' + TEL_HINT
  if (compact.charAt(0) === '+') {
    if (compact.slice(1, 3) === '66') {
      return isThaiTel('0' + compact.slice(3).replace(/^0/, '')) ? ''
        : 'เบอร์ +66 ไม่ครบหรือเกิน — เช่น +66 81 234 5678 หรือ +66 2 123 4567'
    }
    return /^\+[1-9]\d{7,14}$/.test(compact) ? ''
      : 'เบอร์ต่างประเทศต้องเป็น + รหัสประเทศ และเบอร์ รวม 8–15 หลัก เช่น +856 20 5555 1234'
  }
  if (compact.charAt(0) !== '0') return 'เบอร์โทรไม่ถูกต้อง — ' + TEL_HINT
  if (isThaiTel(compact)) return ''
  return 'เบอร์ไทยต้องมี 10 หลัก (มือถือ 06/08/09) หรือ 9 หลัก (บ้าน 02–05, 07) — ' +
    'ถ้าเป็นเบอร์ต่างประเทศ ให้ขึ้นต้นด้วย + และรหัสประเทศ'
}

// A booking occupies its seat from the moment it is made until it is cancelled.
// 'pending' and 'confirmed' are the pre-1.6.0 statuses from when the office had
// to confirm each booking; treating anything-but-cancelled as active is what
// let that step be removed with no data migration.
function isActive (status) {
  return status !== 'cancelled'
}

// The roles that run the delivery operation. One list, because the same people
// need all three powers: set fleet capacity, see every booking, and move any of
// them to any date -- ignoring capacity, Sunday and lead time -- to absorb a
// breakdown or a customer emergency. Everyone else may still move a booking
// they created themselves, but only within the ordinary rules.
//
// This is a UI rule, not a security boundary: Firestore rules are open, so the
// data layer enforces nothing. See the Security section of CLAUDE.md.
const STAFF_ROLES = ['administrator', 'admin', 'manager', 'delivery']

function isStaffRole (role) {
  return STAFF_ROLES.indexOf(role) !== -1
}

// ปิดรับจอง: delivery closes a day to shops before its counters say it is full,
// because the team has judged it has enough work. A flag on bookingDay, not a
// cut in จำนวนรถ -- the fleet that day has not changed, and a car count lowered
// to fake a full day would lie to the planner and to the capacity report.
//
// Only a real `true` stops a day. Firestore rules are open, so a hand-edited
// "true" string is a real input, and reading it as stopped would close a day
// nobody meant to close with nothing on screen able to reopen it cleanly.
function isDayStopped (dayDoc) {
  return !!dayDoc && dayDoc.stopped === true
}

// Who a stopped day refuses. Only shops:
//   - staff keep the last word here as everywhere else on the page, and the
//     page asks them to confirm instead;
//   - an out-of-area booking takes a seat on its ROUTE, not on the day, and a
//     route already has its own ปิดรับ;
//   - a same-day slot change (เช้า -> บ่าย) adds no work to the day.
function checkDayOpen (dayDoc, role, opts) {
  const o = opts || {}
  if (!isDayStopped(dayDoc) || isStaffRole(role) || o.outOfArea || o.sameDay) return { ok: true }
  return { ok: false, reason: 'DAY_STOPPED' }
}

// Who may book an address serviceArea.js does not cover, and how they are asked.
//
//   'refuse'  -- the address is out of area and that is the end of it. A shop
//                books self-service against the small car's routine coverage;
//                letting one promise a customer in ราชบุรี a slot that no trip
//                was planned for is exactly what the area table prevents.
//   'confirm' -- staff are told where the address is and decide. Delivery
//                groups far-province customers -- สมุทรสาคร, ราชบุรี, นครปฐม,
//                กาญจนบุรี -- and runs them together on one day. That is real
//                work the table has no way to describe, so the table stays the
//                routine coverage and staff step outside it deliberately,
//                with the booking flagged so the rest of the system can see it.
//
// Same four roles as every other staff power on the page. Kept here rather than
// inside bookingDate.vue because views are untested: check-booking-rules is what
// holds the line that a shop can never reach 'confirm'.
function areaGateFor (role) {
  return isStaffRole(role) ? 'confirm' : 'refuse'
}

// --- out-of-area trips -------------------------------------------------------
// A trip is a route to one or more provinces on one day. It holds a truck out
// of that day's fleet, fills to a capacity, and closes. serviceArea.js says
// where the small car goes as ROUTINE; a trip is a bounded, one-day extension
// of that, which is what lets the table stay what it is.
const TRIP_OPEN = 'open'
const TRIP_CLOSED = 'closed'
// One truck unless delivery says otherwise. Eight orders in กาญจนบุรี may want
// two, so it is a count and not a boolean.
const DEFAULT_TRIP_CARS = 1

// A tripEntry is the mirror kept on bookingDay.tripByProvince[province]:
// { tripId, count, capacity, status }. Everything below reads that shape rather
// than the trip document, because it is the shape every screen already has.
function tripSeatsLeft (entry) {
  if (!entry) return 0
  return Math.max(0, (entry.capacity || 0) - (entry.count || 0))
}

function tripStateOf (entry) {
  if (!entry || !entry.tripId) return 'none'
  // Seats win over status: the mirror carries no autoClosed flag, so a trip
  // that is closed AND out of seats has to read as 'full' -- that is the
  // combination an auto-closed trip's mirror always is in -- while a trip
  // closed early WITH room to spare still reads as 'closed'.
  if (tripSeatsLeft(entry) <= 0) return 'full'
  return entry.status === TRIP_OPEN ? 'open' : 'closed'
}

// What this role may do about an out-of-area address on this day.
//
//   'book'    a route is going there with room -- anyone may take a seat
//   'openNew' no route yet, and this role may create one by booking
//   'full'    a route is going there and every seat is taken
//   'closed'  a route is going there and delivery closed it early
//   'no'      no route, and this role may not create one
//
// 'full' and 'closed' are deliberately not one answer: to a shop they are
// different sentences. รถเต็มแล้ว invites asking for another date; ปิดรับแล้ว
// invites asking delivery.
function tripGateFor (role, entry) {
  const state = tripStateOf(entry)
  if (state === 'open') return 'book'
  if (state === 'none') return isStaffRole(role) ? 'openNew' : 'no'
  return state
}

// An out-of-area booking must sit on a route. This is the backstop the view
// cannot be: bookingDate hands createBooking a null tripId whenever the route is
// full or closed, so without this the transaction sees an out-of-area booking
// with no route and no capacity rule that applies to it, and writes it. The
// booking then takes no seat, reserves no truck, and appears on the board as a
// stray nobody meant to create.
//
// -> { ok: true } | { ok: false, reason: 'NO_TRIP' }
function checkOutOfAreaBooking (outOfArea, tripId, opening) {
  if (!outOfArea) return { ok: true }
  return (tripId || opening) ? { ok: true } : { ok: false, reason: 'NO_TRIP' }
}

// Everything an attach must be true of, in one place, because the only checks
// that existed were in the board's route picker -- and a view is not a rule.
// A route on another date would mirror onto the booking's day and offer shops a
// seat on a truck that is not coming that day.
function checkAttach (booking, trip) {
  if (!trip) return { ok: false, reason: 'NO_TRIP' }
  if (trip.date !== booking.date) return { ok: false, reason: 'TRIP_DATE' }
  if ((trip.provinces || []).indexOf(provinceOf(booking)) === -1) {
    return { ok: false, reason: 'TRIP_PROVINCE' }
  }
  return checkTripSeat(trip)
}

// The day's tripByProvince after one route changes. A closed route releases its
// province so another may be opened there -- which means a route does not
// necessarily still own the entry it wrote. Cancelling a booking on the old,
// closed route must not overwrite the open route that has since taken its
// place, or every shop stops being offered a route that is really running.
function mirrorAfterTrip (dayDoc, tripId, trip) {
  const byProvince = Object.assign({}, (dayDoc && dayDoc.tripByProvince) || {})
  const entry = tripEntryOf(tripId, trip)
  ;(trip.provinces || []).forEach(p => {
    const held = byProvince[p]
    if (held && held.tripId !== tripId && held.status === TRIP_OPEN) return
    byProvince[p] = entry
  })
  return byProvince
}

// The routes running on one day, ONE ENTRY PER ROUTE.
//
// tripByProvince is keyed by province because that is what a lookup needs -- an
// address resolves to a province and asks what is going there. But a route may
// cover several provinces, and then several keys point at the SAME route with
// the same seats. Rendering the map key by key drew one route to สมุทรสงคราม
// and สมุทรสาคร as two chips reading 0/5 and 0/5, which says ten seats when
// there are five and one truck.
//
// -> [{ tripId, provinces: [...], count, capacity, state }], provinces sorted
//    within a route and routes sorted by their first province, so the calendar
//    does not reshuffle between two snapshots of the same day.
function tripsForDay (dayDoc) {
  const byProvince = (dayDoc && dayDoc.tripByProvince) || {}
  const byTrip = {}
  Object.keys(byProvince).forEach(province => {
    const entry = byProvince[province]
    if (tripStateOf(entry) === 'none') return
    if (!byTrip[entry.tripId]) {
      byTrip[entry.tripId] = {
        tripId: entry.tripId,
        provinces: [],
        count: entry.count || 0,
        capacity: entry.capacity || 0,
        state: tripStateOf(entry)
      }
    }
    byTrip[entry.tripId].provinces.push(province)
  })
  return Object.keys(byTrip)
    .map(id => {
      byTrip[id].provinces.sort((a, b) => a.localeCompare(b))
      return byTrip[id]
    })
    .sort((a, b) => a.provinces[0].localeCompare(b.provinces[0]))
}

// The day's tripByProvince after a route is cancelled outright.
//
// A TOMBSTONE rather than a deleted key, and that is not a shortcut: Firestore
// deep-merges map fields, so a key dropped from a locally-built map survives in
// the stored document -- the delete silently does nothing, and the day keeps
// offering a route that is not running. An entry with a null tripId reads as
// 'none' to tripStateOf, which is what every caller already asks, so this
// travels through code paths that are asserted rather than through a field-path
// delete keyed on Thai province names.
//
// Only entries this route still owns are tombstoned. Another route may have
// taken the province over since -- see mirrorAfterTrip.
function mirrorAfterCancel (dayDoc, tripId, trip) {
  const byProvince = Object.assign({}, (dayDoc && dayDoc.tripByProvince) || {})
  ;(trip.provinces || []).forEach(p => {
    const held = byProvince[p]
    if (!held || held.tripId !== tripId) return
    byProvince[p] = { tripId: null, count: 0, capacity: 0, status: TRIP_CLOSED }
  })
  return byProvince
}

// Cancelling a route gave its truck back, and nothing here can take one again --
// reopening is a status change, not an opening, so it never touches
// carsReserved. A cancelled route therefore cannot come back: it would be open,
// offered to shops, and running on a truck the day has already re-let. Open a
// fresh route instead.
function checkReopen (trip) {
  if (!trip) return { ok: false, reason: 'NO_TRIP' }
  if (trip.cancelled) return { ok: false, reason: 'TRIP_CANCELLED' }
  return { ok: true }
}

// Read once, here, because four call sites had grown their own copy of it.
function reservedCars (dayDoc) {
  return Math.max(0, Math.floor(Number(dayDoc && dayDoc.carsReserved) || 0))
}

// --- the later days of a two-day route ---------------------------------------
// A route sent to คิวรถเล็ก as 2 days (sendTripToDispatch / setTripDays) keeps
// its car out on the day after it leaves. carsReserved is the DEPARTURE day's
// counter, owned by openTrip / cancelTrip, so the later days get their own
// mirror: bookingDay.carriedRoutes[tripId] on each of them. capacityForDay and
// checkTripOpen subtract it, so shops cannot fill day 2 with Bangkok work for a
// car that is still on the road -- the planner already left it out
// (dispatchPlan.carriedPlates), the calendar did not.
//
// Keyed by route, so writing it is idempotent: sending again, or แก้จำนวนวัน
// twice, sets the same entry. Released as { held: false } rather than deleted,
// for the reason mirrorAfterCancel tombstones: the writes are sets with merge,
// and a dropped map key survives in the stored document.
//
// It takes no seat on day 2 -- the route's bookings stay on its departure day.
const MAX_ROUTE_DAYS = 2

function carriedRoutesOf (dayDoc) {
  const map = (dayDoc && dayDoc.carriedRoutes) || {}
  return Object.keys(map)
    .filter(id => map[id] && map[id].held === true)
    .map(id => Object.assign({ tripId: id }, map[id], { provinces: (map[id].provinces || []).slice().sort((a, b) => a.localeCompare(b)) }))
    .sort((a, b) => String(a.provinces[0] || '').localeCompare(String(b.provinces[0] || '')) || a.tripId.localeCompare(b.tripId))
}

function carriedCars (dayDoc) {
  return carriedRoutesOf(dayDoc).length
}

// The carriedRoutes entries a route needs when it runs `days` days: held on
// each later day it runs, released on each it no longer does. Always every
// later day up to MAX_ROUTE_DAYS, so 2 -> 1 releases what 1 -> 2 held.
// `trip` must carry date, regNum and provinces.
// -> [{ date, entry }]
function routeHoldWrites (tripId, trip, days) {
  const n = Math.min(MAX_ROUTE_DAYS, Math.max(1, Math.floor(Number(days)) || 1))
  const out = []
  for (let i = 1; i < MAX_ROUTE_DAYS; i++) {
    const date = moment.tz(trip.date, TZ).add(i, 'days').format('YYYY-MM-DD')
    out.push({
      date,
      entry: i < n
        ? { held: true, regNum: trip.regNum || '', provinces: (trip.provinces || []).slice(), from: trip.date, day: i + 1, days: n }
        : { held: false }
    })
  }
  return out
}

// What the page says when a hold pushed its day over: [] when it did not.
function overDayLines (overDay) {
  if (!overDay || !overDay.date) return []
  return [`⚠ วันที่ ${overDay.date} รถคันนี้ยังอยู่บนเส้นทาง จองไว้ ${overDay.total} งาน เกินที่รับได้ ${overDay.cap} งาน — ` +
    'ตรวจสอบและย้ายงานของวันนั้น']
}

// After a hold lands: is that day now booked past what it can take? A warning,
// never a refusal -- the car really is away, refusing the send changes nothing.
function overAfterHold (dayDoc, configCars, configPerCar) {
  const total = Number(dayDoc && dayDoc.total) || 0
  return total > capacityForDay(dayDoc, configCars, configPerCar).total
}

// Everything that decides whether a trip may be opened, from ONE day document.
// That constraint is not incidental: a Firestore transaction cannot run a
// query, so "at most one open trip per (date, province)" is only enforceable if
// the claims live on the day. Without it, two overlapping ราชบุรี trips both
// match the same address and nothing can say which one a booking belongs on.
//
// -> { ok: true, provinces, cars, capacity }   the normalised values to write
//    { ok: false, reason, province? }
//
// `date` is checked here rather than in the dialog because the dialog is a bare
// type="date" field that validates nothing: a route opened for last Tuesday is
// invisible to subscribeTrips, which starts at today, so nobody can close it,
// cancel it, or get its truck back. A Sunday route is refused for the same
// reason every other Sunday booking is -- nothing is delivered that day.
function checkTripOpen (dayDoc, request, configCars, date) {
  const req = request || {}
  const provinces = (req.provinces || [])
    .map(p => String(p === null || p === undefined ? '' : p).trim())
    .filter(Boolean)
  if (!provinces.length) return { ok: false, reason: 'NO_PROVINCES' }

  // A route to a province the small car already serves in full is meaningless,
  // and not harmless: it would reserve a truck and pull those deliveries off
  // the day's ordinary capacity for nothing. Refused here rather than only
  // hidden from the picker -- a guard that lives only in a .vue file is not a
  // guard, which this branch has already learned once.
  //
  // Partly-open provinces stay allowed. นครปฐม is exactly what a route is FOR:
  // the ตำบล the service-area table leaves out.
  const served = provinces.find(p => isFullyServedProvince(p))
  if (served) return { ok: false, reason: 'PROVINCE_IN_AREA', province: served }

  const capacity = Math.floor(Number(req.capacity))
  if (!isFinite(capacity) || capacity < 1) return { ok: false, reason: 'BAD_CAPACITY' }

  const cars = Math.max(1, Math.floor(Number(req.cars) || 0) || DEFAULT_TRIP_CARS)

  if (isSunday(date)) return { ok: false, reason: 'SUNDAY' }
  if (date < todayStr()) return { ok: false, reason: 'PAST' }

  const day = dayDoc || {}
  const byProvince = day.tripByProvince || {}
  // A closed trip leaves its entry behind so the board can still show it; it
  // does not hold the province, because the route is over.
  const clash = provinces.find(p => byProvince[p] && byProvince[p].status === TRIP_OPEN)
  if (clash) return { ok: false, reason: 'TRIP_EXISTS', province: clash }

  const fleet = day.cars != null ? day.cars : configCars
  const reserved = reservedCars(day) + carriedCars(day)
  if ((Math.floor(Number(fleet)) || 0) - reserved - cars < 0) return { ok: false, reason: 'NO_CARS' }

  return { ok: true, provinces: provinces, cars: cars, capacity: capacity }
}

// Extending a route that is already open. Delivery decides the route on one day
// and learns on the next that กาญจนบุรี is on the way to ราชบุรี; without this
// the only ways to say so were a second route, which holds a second truck for
// what is one drive, or cancelling the first, which cancelTrip refuses once
// anyone has booked on it.
//
// It takes no truck and changes no capacity: the route is still one drive with
// the seats delivery gave it, and แก้จำนวนงาน is the separate decision about
// whether the new province needs more. The same refusals as checkTripOpen apply
// to each new province, for the same reasons -- a province the small car
// already serves, or one another open route on that day already holds.
//
// Provinces the route already has are dropped rather than refused, so picking
// one twice is harmless; asking for nothing new at all is refused, because the
// save would otherwise report a success that changed nothing.
//
// -> { ok: true, provinces, added }   the full new list, and what it gained
//    { ok: false, reason, province? }
function checkTripAddProvinces (dayDoc, tripId, trip, provinces) {
  if (!trip) return { ok: false, reason: 'NO_TRIP' }
  // A cancelled route gave its truck back. Adding to it would offer shops a
  // seat on a route nothing is driving.
  if (trip.cancelled) return { ok: false, reason: 'TRIP_CANCELLED' }
  // Nothing about a finished day can still change. subscribeTrips starts at
  // today, so the board never offers this, but the rule does not rely on that.
  if (trip.date < todayStr()) return { ok: false, reason: 'PAST' }

  const wanted = (provinces || [])
    .map(p => String(p === null || p === undefined ? '' : p).trim())
    .filter(Boolean)
  if (!wanted.length) return { ok: false, reason: 'NO_PROVINCES' }

  const current = trip.provinces || []
  const added = []
  wanted.forEach(p => { if (current.indexOf(p) === -1 && added.indexOf(p) === -1) added.push(p) })
  if (!added.length) return { ok: false, reason: 'TRIP_HAS_PROVINCE' }

  const served = added.find(p => isFullyServedProvince(p))
  if (served) return { ok: false, reason: 'PROVINCE_IN_AREA', province: served }

  // Another OPEN route holding the province is the clash. A closed one has only
  // left its entry behind for the board, exactly as in checkTripOpen, and this
  // route's own entries cannot clash with it.
  const byProvince = (dayDoc && dayDoc.tripByProvince) || {}
  const clash = added.find(p => byProvince[p] && byProvince[p].tripId &&
    byProvince[p].tripId !== tripId && byProvince[p].status === TRIP_OPEN)
  if (clash) return { ok: false, reason: 'TRIP_EXISTS', province: clash }

  return { ok: true, provinces: current.concat(added), added: added }
}

// Taking a province back off a route -- the undo for checkTripAddProvinces, and
// for a province picked at opening that turned out not to be on the way.
//
// Refused while any active booking on the route is in that province: those
// customers were promised this route, and removing the province would leave
// them on a truck that no longer lists where they live. Move or cancel them
// first -- the same line cancelTrip draws for the whole route. The last
// province cannot go either; a route to nowhere is what ยกเลิกเส้นทาง is for.
//
// `bookings` is every active booking the caller found on the route, by query,
// OUTSIDE the transaction, because a transaction cannot run one. `count` on the
// trip is moved in the same transaction as every seat, so if the query saw
// fewer bookings than the trip counts, one landed after the query and may be in
// this very province: refused as TRIP_STALE, to be retried. More than the
// count is harmless -- nothing unseen -- and is let through.
//
// -> { ok: true, provinces }   the list without the province
//    { ok: false, reason, count? }
function checkTripRemoveProvince (trip, province, bookings) {
  if (!trip) return { ok: false, reason: 'NO_TRIP' }
  if (trip.cancelled) return { ok: false, reason: 'TRIP_CANCELLED' }
  if (trip.date < todayStr()) return { ok: false, reason: 'PAST' }

  const p = String(province === null || province === undefined ? '' : province).trim()
  const current = trip.provinces || []
  if (current.indexOf(p) === -1) return { ok: false, reason: 'TRIP_PROVINCE' }
  if (current.length <= 1) return { ok: false, reason: 'TRIP_LAST_PROVINCE' }

  const active = (bookings || []).filter(b => isActive(b.status))
  if (active.length < (trip.count || 0)) return { ok: false, reason: 'TRIP_STALE' }

  const inProvince = active.filter(b => provinceOf(b) === p).length
  if (inProvince) return { ok: false, reason: 'PROVINCE_HAS_BOOKINGS', count: inProvince }

  return { ok: true, provinces: current.filter(x => x !== p) }
}

// May a booking take a seat on this trip? Read inside the transaction, exactly
// as checkCapacity is for an ordinary day: the UI is never trusted, and a tab
// left open while the trip filled will offer a seat this refuses.
function checkTripSeat (trip) {
  if (!trip) return { ok: false, reason: 'NO_TRIP' }
  if (trip.status !== TRIP_OPEN) {
    // Closed BY the last booking and closed BY a person are different sentences
    // to whoever is being refused.
    return { ok: false, reason: trip.autoClosed ? 'TRIP_FULL' : 'TRIP_CLOSED_ERR' }
  }
  if ((trip.count || 0) >= (trip.capacity || 0)) return { ok: false, reason: 'TRIP_FULL' }
  return { ok: true }
}

// The trip document after a seat is taken. Closing happens in the SAME
// transaction that takes the last seat, so there is never a moment when a full
// trip is still being offered.
function tripAfterSeat (trip) {
  const count = (trip.count || 0) + 1
  const full = count >= (trip.capacity || 0)
  return Object.assign({}, trip, {
    count: count,
    status: full ? TRIP_CLOSED : trip.status,
    autoClosed: full ? true : !!trip.autoClosed
  })
}

// And after one is given back. Only a trip the SYSTEM closed reopens: "we are
// leaving with three" is a decision a cancellation does not get to undo, and
// `autoClosed` is what tells the two apart.
function tripAfterRelease (trip) {
  const count = Math.max(0, (trip.count || 0) - 1)
  const reopen = !!trip.autoClosed && count < (trip.capacity || 0)
  return Object.assign({}, trip, {
    count: count,
    status: reopen ? TRIP_OPEN : trip.status,
    autoClosed: reopen ? false : !!trip.autoClosed
  })
}

// The trip document after delivery changes its capacity. Lowering it below what
// is already booked is allowed and closes the route -- the same way the จำนวนรถ
// editor lets a day be cut below its bookings and flags it, rather than refusing
// a real decision. Raising it above a full route reopens one the system closed,
// and leaves a hand-closed one closed, for the same reason tripAfterRelease
// does: "we are leaving with three" is not undone by an edit to a number.
function tripAfterCapacity (trip, capacity) {
  const full = (trip.count || 0) >= capacity
  const reopen = !full && !!trip.autoClosed
  return Object.assign({}, trip, {
    capacity: capacity,
    status: full ? TRIP_CLOSED : (reopen ? TRIP_OPEN : trip.status),
    autoClosed: full ? true : (reopen ? false : !!trip.autoClosed)
  })
}

// What the trip looks like on bookingDay.tripByProvince[province]. Four fields:
// everything tripGateFor and a day card need, and nothing else. The trip
// document is the truth; this is the copy every screen already has in hand.
function tripEntryOf (tripId, trip) {
  return {
    tripId: tripId,
    count: trip.count || 0,
    capacity: trip.capacity || 0,
    status: trip.status
  }
}

// Where far-province work is gathered, and the heading for the bookings that
// cannot be gathered anywhere. An SO whose free-text address named no single
// ตำบล is stored with an empty `location`, and those still need planning -- so
// they get one heading of their own rather than being scattered as blanks or
// dropped.
const NO_PROVINCE = 'ไม่ระบุจังหวัด'

function provinceOf (booking) {
  const loc = parseLocation(booking && booking.location)
  return (loc && loc.province) || NO_PROVINCE
}

// Bookings -> one entry per province, `{ province, count, rows }`, biggest group
// first: the group worth sending a truck to is the one with the most customers
// waiting in it. Ties break by name, so the strip does not reshuffle itself
// between two snapshots of the same data -- movement with no cause is how a
// screen starts to feel broken. NO_PROVINCE sorts last however big it grows,
// because it is not a place anyone can send a truck to.
//
// Rows keep the order they arrived in, which is compareForDispatch's -- the
// caller sorts before grouping, and grouping must not undo it.
function groupByProvince (bookings) {
  const byProvince = {}
  const order = []
  ;(bookings || []).forEach(b => {
    const province = provinceOf(b)
    if (!byProvince[province]) { byProvince[province] = []; order.push(province) }
    byProvince[province].push(b)
  })
  return order
    .map(province => ({ province: province, count: byProvince[province].length, rows: byProvince[province] }))
    .sort((a, b) => {
      if (a.province === NO_PROVINCE) return b.province === NO_PROVINCE ? 0 : 1
      if (b.province === NO_PROVINCE) return -1
      return b.count - a.count || a.province.localeCompare(b.province)
    })
}

const MOVE_REASONS = [
  { value: 'customer', text: 'ลูกค้าขอเลื่อน' },
  { value: 'unavailable', text: 'ลูกค้าไม่สะดวกรับของ' },
  { value: 'noCar', text: 'รถไม่พอ' },
  { value: 'stock', text: 'สินค้าไม่พร้อมส่ง' },
  { value: 'other', text: 'อื่นๆ' }
]

// Counter arithmetic for a move. The booking is removed from the day BEFORE the
// target slot is tested -- otherwise a day sitting exactly on its total cap
// would refuse an เช้า -> บ่าย change that does not alter that total at all.
// `outOfArea` is counted on its own line and charged no seat. A ราชบุรี run is
// one truck gone for the day, not one of the day's slots, and delivery groups
// several such customers into that single trip -- so charging each of them a
// seat would claim five trucks were needed, while charging none of them at all
// would leave the trip invisible on the calendar. The slot such a booking
// carries is still the window the customer was promised; it just does not move
// am / pm / fullDay / total. Delivery reserves the truck itself, through the
// จำนวนรถ override that already exists.
const ZERO_COUNTS = { am: 0, pm: 0, fullDay: 0, total: 0, outOfArea: 0 }

function countsAfterRemove (counts, slot, outOfArea) {
  const c = Object.assign({}, ZERO_COUNTS, counts || {})
  const drop = (n, hit) => Math.max(0, (n || 0) - (hit ? 1 : 0))
  if (outOfArea) {
    return {
      am: c.am, pm: c.pm, fullDay: c.fullDay, total: c.total, outOfArea: drop(c.outOfArea, true)
    }
  }
  return {
    am: drop(c.am, slot === 'am'),
    pm: drop(c.pm, slot === 'pm'),
    fullDay: drop(c.fullDay, slot === 'full'),
    total: drop(c.total, true),
    outOfArea: c.outOfArea || 0
  }
}

function countsAfterAdd (counts, slot, outOfArea) {
  const c = Object.assign({}, ZERO_COUNTS, counts || {})
  if (outOfArea) {
    return {
      am: c.am, pm: c.pm, fullDay: c.fullDay, total: c.total, outOfArea: (c.outOfArea || 0) + 1
    }
  }
  return {
    am: (c.am || 0) + (slot === 'am' ? 1 : 0),
    pm: (c.pm || 0) + (slot === 'pm' ? 1 : 0),
    fullDay: (c.fullDay || 0) + (slot === 'full' ? 1 : 0),
    total: (c.total || 0) + 1,
    outOfArea: c.outOfArea || 0
  }
}

// The order a day is worked in. The list is read one day at a time now, so
// every row ties on `date` and the order Firestore happens to return them in
// would otherwise be the order staff read them in -- which changes between
// snapshots of the same day.
//
// ทั้งวัน, then เช้า, then บ่าย. That is SLOTS' own order, taken from SLOTS
// rather than restated, so the two cannot drift apart. An unrecognised slot
// sorts LAST: indexOf gives it -1, which would otherwise put it ahead of ทั้งวัน.
function slotRank (slot) {
  const i = SLOTS.indexOf(slot)
  return i === -1 ? SLOTS.length : i
}

// Every field is coalesced because the list also carries rows written before a
// field existed. One missing `name` throwing inside Array.sort would take the
// whole day's list down, not just that row.
function compareForDispatch (a, b) {
  const x = a || {}
  const y = b || {}
  const byDate = String(x.date || '').localeCompare(String(y.date || ''))
  if (byDate) return byDate
  const bySlot = slotRank(x.slot) - slotRank(y.slot)
  if (bySlot) return bySlot
  // 'th' so ก-ฮ sorts the way a Thai reader expects; code-point order puts
  // vowels that are written before their consonant in the wrong place.
  return String(x.name || '').localeCompare(String(y.name || ''), 'th')
}

// What a later correction may touch. Deliberately narrow: เลขที่ SO is the link
// to SAP and the duplicate-booking guard, and date/slot have capacity
// consequences that belong to moveBooking. Everything here is information the
// customer supplies afterwards -- a landmark they forgot, a pin they finally
// dropped, an extra item -- none of which affects what the day can carry.
// itemCount joins the list because the planner's whole timetable is built on
// it: a booking that arrives as นัดหมาย has no SO to count lines from, and the
// number typed at booking time is a guess until the SO lands 1-3 days before
// delivery. Correcting it must not require cancelling and rebooking.
// installMinutes joins one rung further on. The tiers installTime applies to
// itemCount are an average across a product mix, and a model that installs in
// half the time does not stop doing so because someone also fixed the pin --
// so a duration staff set by hand has to be correctable in its own right.
// The half of the booking form that describes the JOB rather than the customer.
// All five are filled from the SO when there is one, which is what makes them
// the half that must not survive into the next booking: switching
// มีเลขที่ SO -> นัดหมาย, or changing the SO number, starts a different job at a
// different address, and anything still loaded from the last one is wrong
// rather than merely stale. bookingDate.vue blanks exactly this list.
const JOB_FIELDS = ['geo', 'landmark', 'productList', 'itemCount', 'installMinutes']

// The same list, and deliberately the same list. A field that describes the job
// is one a later correction may touch and one the next job must not inherit --
// two questions with one answer. check-booking-rules asserts they stay equal, so
// if they ever have to differ that is a decision someone makes on purpose
// instead of a field quietly missing from one of them.
const EDIT_FIELDS = JOB_FIELDS

// The form's empty state, in one place because it had been written out twice --
// once in data() and once in resetForm() -- and a field added to one of them
// was a field the other silently kept. Both callers now share this, so a new
// field cannot be added without being blankable.
//
// A fresh object every call: two resets sharing one object would alias the same
// form.
function blankBookingForm () {
  const form = { name: '', telephone: '', address: '', location: '', zipcode: '' }
  JOB_FIELDS.forEach(field => { form[field] = '' })
  return form
}

// A นัดหมาย handed over prefilled from another system -- kioskonlineshop's order
// page, where a customer wants a delivery date before the order has a SAP SO and
// so before U_KSK_BookDate can hold anything. The link is
// `#/bookingdate?appt=1&tel=…&name=…&address=…&zipcode=…&items=…&count=…`
// (&geo=&landmark= optional).
//
// It fills the customer and the work, and NOTHING else. No date, no slot: those
// are chosen here against the real capacity, and every rule a typed นัดหมาย
// passes still applies. ตำบล/อำเภอ never goes into the form from here either --
// the zipcode's list is where the service area is checked, and a handed-over
// string could name a ตำบล this page would refuse. It travels separately, as a
// wish (apptLocation), that the list may grant (pickApptLocation).
//
// Every value is treated as untrusted text: trimmed, capped, and dropped when it
// is not what the field holds (a zipcode that is not five digits, an unreadable
// pin, a count that is not a positive number).
function apptFromQuery (query) {
  const q = query || {}
  if (!q.appt) return null
  const str = (v, max) => String(v == null ? '' : v).trim().slice(0, max)
  const form = blankBookingForm()
  form.name = str(q.name, 100)
  form.telephone = str(q.tel, 40)
  form.address = str(q.address, 300)
  form.zipcode = /^\d{5}$/.test(str(q.zipcode, 5)) ? str(q.zipcode, 5) : ''
  form.productList = str(q.items, 1000)
  form.landmark = str(q.landmark, SAP_LANDMARK_MAX)
  form.geo = formatGeo(parseGeo(q.geo))
  const count = Math.floor(Number(q.count))
  form.itemCount = isFinite(count) && count > 0 ? String(count) : ''
  return form
}

// The ตำบล the other system already knows -- `&subdistrict=&district=&province=`
// -- as the value the ตำบล list uses (formatLocation), or '' when it names none.
// All three or nothing: a ตำบล name alone repeats across districts. A comma in
// any part would shift the parts parseLocation reads back, so it drops the wish.
function apptLocation (query) {
  const q = query || {}
  if (!q.appt) return ''
  const parts = [q.subdistrict, q.district, q.province].map(v => String(v == null ? '' : v).trim().slice(0, 100))
  if (parts.some(p => !p || p.indexOf(',') !== -1)) return ''
  return formatLocation({ subDt: parts[0], district: parts[1], province: parts[2] })
}

// Grants the wish only when the zipcode's list offers that exact ตำบล. The list
// is already cut to what this login may book, so an out-of-area ตำบล, or one
// filed under a different zipcode, selects nothing and is picked by hand.
function pickApptLocation (options, wanted) {
  if (!wanted) return ''
  const hit = (options || []).find(o => o && o.value === wanted)
  return hit ? hit.value : ''
}

// Whether a URL may be reported back to: HTTPS at one of the named domains, or
// a subdomain of one -- `host === dom || host.endsWith('.' + dom)`, so
// `evilkioskthailand.com` does not pass as `kioskthailand.com`. Plain HTTP is
// refused except on localhost, which is how the other system is developed.
//
// A domain list rather than exact origins: a new subdomain or a moved path then
// just works, where an origin list silently stops offering the button and
// somebody loses an afternoon to it.
function isAllowedReturn (u, allowedDomains) {
  const host = String(u.hostname || '').toLowerCase()
  if (u.protocol === 'http:') return host === 'localhost' || host === '127.0.0.1'
  if (u.protocol !== 'https:') return false
  return (allowedDomains || []).some(d => {
    const dom = String(d || '').toLowerCase().replace(/^\./, '')
    return !!dom && (host === dom || host.endsWith('.' + dom))
  })
}

// Where a prefilled นัดหมาย came from, and where to send the result back to.
// `ref` is the other system's own order number, stored on the booking as
// `sourceRef` so the two records stay linked even if the return trip never
// happens -- today the only link between them is the customer's phone.
//
// The return URL is checked (isAllowedReturn) rather than taken as given. Login
// already keeps strangers off this page, so this is not protecting the booking
// -- which carries no customer detail anyway. It stops a link mailed to a
// signed-in member of staff from walking them to a lookalike login on the
// authority of the internal system.
//
// A refused URL comes back in `rejected` instead of `url`, because the page has
// to SAY so: silently not offering the button is how a misconfigured domain
// hides for a week. The booking itself is never affected either way.
function apptReturn (query, allowedDomains) {
  const q = query || {}
  if (!q.appt) return null
  const ref = String(q.ref == null ? '' : q.ref).trim().slice(0, 64)
  const raw = String(q.return == null ? '' : q.return).trim()
  const none = { ref: ref, url: '', host: '', rejected: '' }
  if (!raw) return ref ? none : null
  let u
  try { u = new URL(raw) } catch (e) { return Object.assign({}, none, { rejected: raw.slice(0, 80) }) }
  if (!isAllowedReturn(u, allowedDomains)) {
    return Object.assign({}, none, { rejected: (u.host || raw).slice(0, 80) })
  }
  return { ref: ref, url: u.toString(), host: u.host, rejected: '' }
}

// The return URL with the booking's result on it, for the button the page shows
// after a successful save. Whatever query the caller's own URL carried is kept.
function apptReturnUrl (ret, result) {
  if (!ret || !ret.url) return ''
  const r = result || {}
  let u
  try { u = new URL(ret.url) } catch (e) { return '' }
  if (ret.ref) u.searchParams.set('bookedRef', ret.ref)
  if (r.date) u.searchParams.set('date', r.date)
  if (r.slot) u.searchParams.set('slot', r.slot)
  if (r.bookingId) u.searchParams.set('bookingId', r.bookingId)
  return u.toString()
}

const EDIT_FIELD_LABEL_TH = {
  geo: 'พิกัด',
  landmark: 'จุดสังเกต',
  productList: 'รายการสินค้า',
  itemCount: 'จำนวนชิ้น',
  installMinutes: 'เวลาติดตั้ง (นาที)',
  // The customer half. Only an attach rewrites these -- a correction still
  // cannot -- but the history line and the confirm dialog both read this map,
  // so a field with no label here reaches staff as its English key.
  name: 'ชื่อลูกค้า',
  telephone: 'เบอร์โทร',
  address: 'ที่อยู่',
  location: 'ตำบล/อำเภอ/จังหวัด',
  zipcode: 'รหัสไปรษณีย์'
}

// A นัดหมาย is booked before the SO exists: delivery holds the day, and the SO
// number lands one to three days before it. Everything typed at booking time --
// the customer's name as the shop heard it, an address read over the phone, a
// guess at how many items -- was standing in for what the SO actually says, so
// attaching one replaces the lot.
//
// date, slot and installMinutes are NOT here, and that is the point: attaching
// an SO is not a reschedule (no bookingDay document is touched, no capacity
// moves) and an SO carries nothing that contradicts a duration a person set by
// hand.
const SO_FIELDS = ['name', 'telephone', 'address', 'location', 'zipcode', 'geo', 'landmark',
  'productList', 'itemCount', 'prodList', 'totalVolume',
  'custName', 'custTel', 'custAddress', 'city', 'cardCode', 'cardName']

// The half of SO_FIELDS a person can read, which is the half the confirm dialog
// shows and the history logs. prodList is an array of SAP rows and totalVolume
// is derived from it; the cust* / card* copies are the raw SAP record kept for
// the dispatch side. "prodList: [object Object] → [object Object]" in a history
// line tells whoever is reading it nothing at all.
const SO_DIFF_FIELDS = ['name', 'telephone', 'address', 'location', 'zipcode', 'geo', 'landmark',
  'productList', 'itemCount']

// Whether the SO actually carried this field. '' and null are absences; 0 is
// not -- a set of product rows that really do total no volume has to be able to
// say so. An empty array counts as an absence for the same reason '' does:
// GetProdList returning nothing is the call having nothing to say.
function hasSoValue (v) {
  if (v == null || v === '') return false
  if (Array.isArray(v) && v.length === 0) return false
  return true
}

// The SO replaces every field it carries, and only those. A field it carries
// nothing for keeps what the booking already had.
//
// That one rule is what protects พิกัด and จุดสังเกต: staff set them in SAP's
// U_KSK_Geolocation / U_KSK_Landmark or append them to custAddress by hand, and
// most orders have neither, so a blank there means "not recorded" rather than "no pin" --
// overwriting a coordinate someone dropped with nothing would send a driver
// looking for an address instead of a point. The same rule covers a รายการสินค้า
// typed by hand when GetProdList is down, which must survive that rather than
// be blanked by it.
function mergeSoFields (booking, incoming) {
  const out = Object.assign({}, booking)
  SO_FIELDS.forEach(f => {
    const v = incoming ? incoming[f] : undefined
    if (hasSoValue(v)) out[f] = v
  })
  return out
}

// Missing, null and '' are one value: null is what Vuetify's clearable writes
// and '' is what the form starts with, so telling them apart would log edits
// that changed nothing. Values are trimmed, and the trimmed value is what the
// caller stores -- diff and write must agree or the next diff reappears.
function editableValue (obj, field) {
  const v = obj ? obj[field] : ''
  return v == null ? '' : String(v).trim()
}

// `fields` defaults to EDIT_FIELDS because a correction is what this was written
// for, but the attach preview asks the same question of SO_DIFF_FIELDS: what
// would change, and from what. One function, so the dialog staff confirm and
// the history line written afterwards can never describe different changes.
function diffFields (before, after, fields) {
  const out = [];
  (fields || EDIT_FIELDS).forEach(f => {
    const from = editableValue(before, f)
    const to = editableValue(after, f)
    if (from !== to) out.push({ field: f, from: from, to: to })
  })
  return out
}

module.exports = {
  BANGKOK, formatLocation, parseLocation, locationLabel, parseGeo, formatGeo, parseCustAddress,
  soPin, soAddressFields, SAP_LANDMARK_MAX, sapPinPatch, sapPinUpdate, sapSoNumbers, sapBookDate, sapRtBookDate,
  TZ, PER_CAR, DEFAULT_CARS, LEAD_WORKING_DAYS, BOOKING_HORIZON_DAYS, MAX_HORIZON_DAYS,
  capacityForCars, capacityForDay, normalizePerCar, withDerivedTotal, normalizeLeadDays, normalizeHorizonDays, errorMessage, isSunday, isSundayOpen, isClosedSunday, checkSundayOpen, stopNoteRequired, capacityDates, todayStr, horizonDate, earliestDeliveryDate, firstBookableDate,
  hasBookableDay, bookingMonthRange,
  SLOTS, SLOT_LABEL_TH, SLOT_TIME, BOOKING_ERROR_TH, BookingError, checkCapacity,
  datesInRange, normalizeTel, telProblem, firstTelPart, hasSearchTerms, isActive, JOB_FIELDS, blankBookingForm, apptFromQuery, apptLocation, pickApptLocation, apptReturn, apptReturnUrl,
  STAFF_ROLES, isStaffRole, isDayStopped, checkDayOpen, areaGateFor, NO_PROVINCE, provinceOf, groupByProvince,
  TRIP_OPEN, TRIP_CLOSED, DEFAULT_TRIP_CARS,
  tripSeatsLeft, tripStateOf, tripGateFor, checkTripOpen,
  checkOutOfAreaBooking, checkAttach, mirrorAfterTrip, mirrorAfterCancel, checkReopen, reservedCars, MAX_ROUTE_DAYS, carriedRoutesOf, carriedCars, routeHoldWrites, overAfterHold, overDayLines,
  checkTripAddProvinces, checkTripRemoveProvince,
  tripsForDay,
  checkTripSeat, tripAfterSeat, tripAfterRelease, tripAfterCapacity, tripEntryOf,
  MOVE_REASONS,
  countsAfterRemove, countsAfterAdd, compareForDispatch, EDIT_FIELDS, EDIT_FIELD_LABEL_TH,
  editableValue, diffFields, SO_FIELDS, SO_DIFF_FIELDS, mergeSoFields
}
